"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  DiagramDownload,
  useDiagramRef,
} from "@/components/tools/geometry/diagram";
import { type Format, formatMeasure } from "@/components/tools/geometry/format";
import { TrigDiagram } from "@/components/tools/geometry/trig-diagram";
import type { TrigStrings } from "@/components/tools/trigonometry-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type AngleInputUnit,
  parseAngleInput,
} from "@/lib/tool-kit/geometry/angle-input";
import {
  type InverseFunction,
  type InverseSolution,
  parseExactInput,
  solveForward,
  solveInverse,
  type TrigForward,
} from "@/lib/tool-kit/geometry/trig-solve";
import {
  formatRationalNice,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

type Mark = "." | ",";

/** An exact degree value as text, or the decimal when only that exists. */
function degreeText(
  exact: Rational | null,
  value: number,
  mark: Mark,
  fmt: Format,
  places: number
): string {
  if (exact) {
    const nice = formatRationalNice(exact, mark);
    return nice.includes("/")
      ? `${nice} ≈ ${formatMeasure(fmt, toNumberR(exact), places)}`
      : nice;
  }
  return formatMeasure(fmt, value, places);
}

function WorkingBox({ lines }: { lines: string[] }) {
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

const SIGN_FUNCTIONS = ["sin", "cos", "tan"] as const;

function referenceLine(result: TrigForward, mark: Mark, fmt: Format): string {
  const shown = (value: number, exact: Rational | null) =>
    exact ? formatRationalNice(exact, mark) : formatMeasure(fmt, value, 4);
  const theta = shown(result.coterminal, result.coterminalExact);
  const ref = shown(result.reference, result.referenceExact);
  const forms = {
    1: `θ′ = θ = ${ref}°`,
    2: `θ′ = 180° − θ = 180° − ${theta}° = ${ref}°`,
    3: `θ′ = θ − 180° = ${theta}° − 180° = ${ref}°`,
    4: `θ′ = 360° − θ = 360° − ${theta}° = ${ref}°`,
  } as const;
  return forms[result.quadrant];
}

function forwardWorking(
  result: TrigForward,
  mark: Mark,
  fmt: Format,
  places: number
): string[] {
  const shown = (value: number, exact: Rational | null) =>
    degreeText(exact, value, mark, fmt, 4);
  const lines: string[] = [];
  const entered = shown(result.degrees, result.exactDegrees);
  const reduced = shown(result.coterminal, result.coterminalExact);
  if (Math.abs(result.degrees - result.coterminal) > 1e-9) {
    lines.push(`θ = ${entered}°, coterminal with ${reduced}°`);
  }
  lines.push(referenceLine(result, mark, fmt));
  const ref = shown(result.reference, result.referenceExact);
  for (const fn of SIGN_FUNCTIONS) {
    const row = result.rows.find((r) => r.fn === fn);
    if (!row) {
      continue;
    }
    const value = row.value;
    if (row.exact !== null) {
      const negative = value !== null && value < 0;
      lines.push(
        `${fn} ${reduced}° = ${negative ? "−" : "+"}${fn} ${ref}° = ${row.exact}`
      );
    } else {
      lines.push(
        `${fn} ${reduced}° ≈ ${value === null ? "—" : formatMeasure(fmt, value, places)}`
      );
    }
  }
  return lines;
}

function quadrantText(result: TrigForward, strings: TrigStrings): string {
  return result.axis ? strings.onAxis : strings.quadrantNames[result.quadrant];
}

function valueOrUndefined(
  value: number | null,
  strings: TrigStrings,
  fmt: Format,
  places: number
): string {
  return value === null
    ? strings.undefinedText
    : formatMeasure(fmt, value, places);
}

export function ForwardView({
  angleText,
  angleUnit,
  strings,
  mark,
  fmt,
  places,
}: {
  angleText: string;
  angleUnit: AngleInputUnit;
  strings: TrigStrings;
  mark: Mark;
  fmt: Format;
  places: number;
}) {
  const svgRef = useDiagramRef();
  if (angleText.trim() === "") {
    return <Notice>{strings.enter}</Notice>;
  }
  const angle = parseAngleInput(angleText, angleUnit);
  if (angle === null) {
    return <ErrorNotice>{strings.invalidAngle}</ErrorNotice>;
  }
  const result = solveForward(angle);
  const rows = result.rows.map((row) => [
    strings.functionNames[row.fn],
    row.exact === "undefined" ? strings.undefinedText : (row.exact ?? "—"),
    valueOrUndefined(row.value, strings, fmt, places),
  ]);
  const get = (fn: string) => result.rows.find((row) => row.fn === fn);
  const label = (fn: "sin" | "cos" | "tan") => {
    const row = get(fn);
    const text =
      row?.exact ?? valueOrUndefined(row?.value ?? null, strings, fmt, 4);
    return `${strings.functionNames[fn]} = ${text === "undefined" ? strings.undefinedText : text}`;
  };
  const angleShown = `${degreeText(result.exactDegrees, result.degrees, mark, fmt, places)}°`;
  const summary = t(strings.summaryForward, {
    angle: angleText.trim(),
    sin:
      get("sin")?.exact ??
      valueOrUndefined(get("sin")?.value ?? null, strings, fmt, places),
    cos:
      get("cos")?.exact ??
      valueOrUndefined(get("cos")?.value ?? null, strings, fmt, places),
    tan:
      get("tan")?.exact ??
      valueOrUndefined(get("tan")?.value ?? null, strings, fmt, places),
  });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.functionNames.sin}
          value={
            get("sin")?.exact ??
            valueOrUndefined(get("sin")?.value ?? null, strings, fmt, places)
          }
        />
        <StatCard
          label={strings.functionNames.cos}
          value={
            get("cos")?.exact ??
            valueOrUndefined(get("cos")?.value ?? null, strings, fmt, places)
          }
        />
        <StatCard
          label={strings.functionNames.tan}
          value={
            get("tan")?.exact === "undefined"
              ? strings.undefinedText
              : (get("tan")?.exact ??
                valueOrUndefined(
                  get("tan")?.value ?? null,
                  strings,
                  fmt,
                  places
                ))
          }
        />
      </StatGrid>
      <DataTable
        head={[
          strings.functionHeader,
          strings.exactHeader,
          strings.decimalHeader,
        ]}
        rows={rows}
      />
      <StatGrid>
        <StatCard label={strings.angleCard} value={angleShown} />
        <StatCard
          label={strings.coterminalCard}
          value={`${degreeText(result.coterminalExact, result.coterminal, mark, fmt, places)}°`}
        />
        <StatCard
          label={strings.referenceCard}
          value={`${degreeText(result.referenceExact, result.reference, mark, fmt, places)}°`}
        />
        <StatCard
          label={strings.quadrantCard}
          value={quadrantText(result, strings)}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">
        {result.axis ? strings.axisNote : strings.positiveIn[result.quadrant]}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingBox lines={forwardWorking(result, mark, fmt, places)} />
      <h3 className={WIDGET_H3}>{strings.figureHeading}</h3>
      <div className="space-y-2">
        <TrigDiagram
          degrees={result.coterminal}
          labels={{
            cos: label("cos"),
            sin: label("sin"),
            tan: label("tan"),
          }}
          svgRef={svgRef}
          title={t(strings.figureTitle, { angle: angleText.trim() })}
        />
        <DiagramDownload
          fileName="trig-unit-circle"
          label={strings.downloadFigure}
          svgRef={svgRef}
        />
      </div>
    </>
  );
}

function solutionText(
  item: InverseSolution,
  mark: Mark,
  fmt: Format,
  places: number
): string {
  const degrees = degreeText(item.exact, item.degrees, mark, fmt, places);
  const radians = item.exact
    ? item.radians
    : `${formatMeasure(fmt, (item.degrees * Math.PI) / 180, places)}`;
  return `${degrees}° = ${radians}${item.exact ? "" : " rad"}`;
}

function generalText(
  fn: InverseFunction,
  all: InverseSolution[],
  strings: TrigStrings,
  mark: Mark,
  fmt: Format,
  places: number
): string {
  const plain = (item: InverseSolution) =>
    `${degreeText(item.exact, item.degrees, mark, fmt, places)}°`;
  if (all.length === 1) {
    return t(strings.generalSingle, { first: plain(all[0]) });
  }
  if (fn === "atan" || fn === "acot") {
    return t(strings.generalTan, { first: plain(all[0]) });
  }
  return t(strings.generalOne, { first: plain(all[0]), second: plain(all[1]) });
}

export function InverseView({
  fn,
  valueText,
  strings,
  mark,
  fmt,
  places,
}: {
  fn: InverseFunction;
  valueText: string;
  strings: TrigStrings;
  mark: Mark;
  fmt: Format;
  places: number;
}) {
  if (valueText.trim() === "") {
    return <Notice>{strings.enter}</Notice>;
  }
  const input = parseExactInput(valueText);
  if (input === null) {
    return <ErrorNotice>{strings.invalidValue}</ErrorNotice>;
  }
  const result = solveInverse(fn, input);
  if (!result.ok) {
    return (
      <ErrorNotice>
        {fn === "asin" || fn === "acos"
          ? strings.domainAsin
          : strings.domainCsc}
      </ErrorNotice>
    );
  }
  const { principal, all } = result;
  const degrees = `${degreeText(principal.exact, principal.degrees, mark, fmt, places)}°`;
  const radians = principal.exact
    ? principal.radians
    : formatMeasure(fmt, (principal.degrees * Math.PI) / 180, places);
  const summary = t(strings.summaryInverse, {
    fn: strings.inverseFunctions[fn].split(" ")[0],
    value: valueText.trim(),
    angle: `${degrees} = ${radians}${principal.exact ? "" : " rad"}`,
  });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.degreesCard} value={degrees} />
        <StatCard
          label={strings.radiansCard}
          value={`${radians}${principal.exact ? "" : " rad"}`}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">{strings.ranges[fn]}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.allHeading}</h3>
      <ul className="list-disc space-y-1 pl-6 font-mono text-foreground text-sm">
        {all.map((item) => (
          <li key={item.degrees}>{solutionText(item, mark, fmt, places)}</li>
        ))}
      </ul>
      <h3 className={WIDGET_H3}>{strings.generalHeading}</h3>
      <p className="font-mono text-foreground text-sm">
        {generalText(fn, all, strings, mark, fmt, places)}
      </p>
    </>
  );
}
